'use client';

import { amplitudeSendEvent, GTM_NOTSET_STRING, pushDataLayer } from '@musinsa/frontend-utils';
import { Button, LoadingDark, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type {
  CreateAccountInfoServiceRequest,
  CreateAccountInfoServiceResponse,
} from '@musinsa/services-curator';
import { createAccountInfoService } from '@musinsa/services-curator';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { useCuratorBroadcastPublisher } from '../../common';
import { useCuratorSnackbar } from '../../common/hooks';
import { useTrackImpression } from '../../common/hooks/useTrackImpression';
import { curatorQueries } from '../../query-factory/index';
import {
  SettlementForm,
  type SettlementFormRef,
} from '../../settlement/components/SettlementInfoForm/SettlementForm';
import { SettlementFormHeader } from '../../settlement/components/SettlementInfoForm/SettlementFormHeader';
import type { SettlementFormData } from '../../settlement/components/SettlementInfoForm/types';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { transformSettlementDataToServiceRequest } from './utils/transformSettlementData';

interface ApiLikeError extends Error {
  data?: { meta?: { message?: string } };
  status?: number;
}

const NETWORK_FAILED_CREATE_MESSAGE =
  '정산 정보 등록에 실패하였습니다. 문제가 계속된다면 PC에서 시도해주세요.';
const CREATE_FAILED_MESSAGE = '정산 정보를 등록하지 못했습니다. 다시 시도해주세요.';

export const SettlementCreateForm = () => {
  const formRef = useRef<SettlementFormRef>(null);
  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const { isWebview, service } = useDeviceInfo();

  const { ref } = useTrackImpression<HTMLDivElement>({
    onImpression: () => {
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.IMPRESSION_CONTENT,
          data: {
            page_name: 'curator_mypage',
            section_name: 'SettlementInformation',
            section_title: '정산 정보',
            position: -1,
          },
        });
      }
    },
  });
  const snackbar = useCuratorSnackbar();

  const { mutate: createAccountInfo, isPending } = useMutation<
    CreateAccountInfoServiceResponse,
    Error,
    SettlementFormData
  >({
    mutationFn: (data: SettlementFormData): Promise<CreateAccountInfoServiceResponse> => {
      const serviceRequest: CreateAccountInfoServiceRequest =
        transformSettlementDataToServiceRequest(data);
      return createAccountInfoService(serviceRequest);
    },
    onSuccess: () => {
      snackbar.show({
        message: '정산 정보를 등록했습니다.',
      });

      triggerQuerySync({
        queryKeys: [curatorQueries.mypage.data().queryKey],
      });
    },
    onError: (error) => {
      console.error('정산정보 등록 실패:', error);
      const apiError = error as ApiLikeError;

      const apiMessage = apiError.data?.meta?.message;
      if (apiMessage) {
        snackbar.error(apiMessage);
        return;
      }

      // HTTP 응답 없음 = 네트워크/업로드 단계 실패 (Android WebView content:// URI 만료 포함).
      // 외부 브라우저로의 복구 경로를 안내한다.
      if (apiError.status === undefined) {
        snackbar.error(NETWORK_FAILED_CREATE_MESSAGE);
        return;
      }

      snackbar.error(CREATE_FAILED_MESSAGE);
    },
  });

  const onFormSubmit = (data: SettlementFormData | Partial<SettlementFormData>) => {
    createAccountInfo(data as SettlementFormData);
  };

  const onSubmitClick = () => {
    if (!isPending) {
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: {
            page_name: 'curator_mypage',
            section_name: 'SettlementInformation',
            section_title: '정산 정보',
            position: 7,
            button_id: 'register',
            button_name: '등록하기',
          },
        });
      }

      if (service === SERVICE.MUSINSA) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'account_upload_complete',
          component_name: 'button',
          event_name: 'click_button',
          from: 'screen_curator_mypage',
          section_name: 'mypage_account',
        });

        pushDataLayer('click_button', {
          section_name: 'mypage_account',
          section_index: '7',
          button_id: 'account_upload_complete',
          index: GTM_NOTSET_STRING,
          brand_id: GTM_NOTSET_STRING,
        });
      }

      formRef.current?.submit();
    }
  };

  return (
    <div className="w-full pt-3 relative" ref={ref}>
      {!isWebview && isPending && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50">
          <LoadingDark size="size24x24" />
        </div>
      )}

      <SettlementFormHeader className="px-4" title="정산 정보 등록" hasTooltip />

      <SettlementForm ref={formRef} onSubmit={onFormSubmit} />

      <div className="px-4">
        <Button
          type="button"
          size={36}
          className="w-full mt-3"
          onClick={onSubmitClick}
          disabled={isPending}
        >
          <Typography variant="body13pxMed">등록하기</Typography>
        </Button>
      </div>
    </div>
  );
};
